// src/api/http.ts
//
// Client HTTP condiviso da tutti i service API.
// Un solo posto in cui vivono: baseURL, Bearer token, formato degli errori.
//
// Il backend risponde SEMPRE in formato RFC 7807 (problem+json):
//   { type, title, status, detail, instance, errors? }
// `detail` è il messaggio pensato per l'utente finale.

import axios, { AxiosInstance, AxiosError, isAxiosError } from 'axios'
import Swal from 'sweetalert2'

const baseURL = import.meta.env.VITE_API_URL

export const http: AxiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 40000,
})

http.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Con FormData il browser deve scegliere da solo il boundary del multipart.
  if (config.data instanceof FormData && config.headers) {
    delete config.headers['Content-Type']
  }
  return config
})

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // 401 = token scaduto o revocato: si torna al login senza mostrare errori tecnici.
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?error=session_expired'
      }
    }
    return Promise.reject(error)
  },
)

/** Corpo di una risposta di errore RFC 7807 (vedi api/bootstrap/app.php). */
export interface ProblemDetails {
  type?: string
  title?: string
  status?: number
  detail?: string
  message?: string
  errors?: Record<string, string[]>
}

function problemOf(error: unknown): ProblemDetails | undefined {
  return isAxiosError<ProblemDetails>(error) ? error.response?.data : undefined
}

/** Estrae il messaggio da mostrare all'utente da un errore Axios. */
export function apiErrorMessage(error: unknown): string {
  const data = problemOf(error)
  if (data?.detail) return data.detail
  if (data?.message) return data.message
  if (isAxiosError(error)) {
    if (error.code === 'ECONNABORTED') return 'Il server non ha risposto in tempo. Riprova.'
    if (error.message === 'Network Error') return 'Impossibile raggiungere il server.'
  }
  return error instanceof Error && error.message ? error.message : 'Errore sconosciuto'
}

/** Errori di validazione per campo (422), pronti da agganciare ai form. */
export function apiValidationErrors(error: unknown): Record<string, string[]> {
  return problemOf(error)?.errors ?? {}
}

/** Mostra l'errore con SweetAlert e lo rilancia, per non ingoiare mai un fallimento. */
export function notifyError(error: unknown): never {
  Swal.fire({
    text: apiErrorMessage(error),
    icon: 'error',
    confirmButtonText: 'Ok',
  })
  throw error
}

export function notifySuccess(text: string): void {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'success',
    title: text,
    showConfirmButton: false,
    timer: 2200,
    timerProgressBar: true,
  })
}

/** Avviso che non blocca (es. numero ordine già usato): resta a video più a lungo. */
export function notifyWarning(text: string): void {
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon: 'warning',
    title: text,
    showConfirmButton: false,
    timer: 5000,
    timerProgressBar: true,
  })
}

/**
 * Scarica un file dall'API (export CSV). Serve il token, quindi niente link
 * diretto: si scarica come blob e lo si salva col nome dato dal server
 * (Content-Disposition, esposto via CORS in api/config/cors.php).
 */
export async function downloadFile(url: string, params?: Record<string, unknown>, fallbackName = 'export.csv'): Promise<void> {
  try {
    const response = await http.get(url, { params, responseType: 'blob', timeout: 120000 })
    const disposition = String(response.headers['content-disposition'] ?? '')
    const nome = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposition)?.[1]
    const href = URL.createObjectURL(response.data as Blob)
    const link = document.createElement('a')
    link.href = href
    link.download = nome ? decodeURIComponent(nome) : fallbackName
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(href), 1000)
  } catch (error) {
    // Con responseType 'blob' anche l'errore problem+json arriva come Blob:
    // lo si rilegge come JSON perché notifyError ne mostri il `detail`.
    if (isAxiosError(error) && error.response?.data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await error.response.data.text())
      } catch {
        // Corpo non JSON: resta il messaggio generico.
      }
    }
    return notifyError(error)
  }
}

/** Risposta paginata standard delle liste (contratto HandlesTableQuery lato Laravel). */
export interface Paginated<T> {
  data: T[]
  meta: {
    current_page: number
    per_page: number
    total: number
    last_page: number
    from: number | null
    to: number | null
  }
}

/** Parametri accettati da tutte le liste. */
export interface TableQuery {
  page?: number
  per_page?: number
  sort?: string
  direction?: 'asc' | 'desc'
  q?: string
  filter?: Record<string, unknown>
}
